// Client for the billing endpoints of the new billing flow (Billing:NewFlow),
// all under /api/Payment. Every route the flow added is here, one function each.
//
// It talks to the shared axios instance directly rather than through
// httpService, because these endpoints answer the way that wrapper cannot
// report: with real HTTP statuses (400, 401, 402, 403, 404, 409), and with a
// machine `code` — and sometimes the `field` it is about — beside the message.
// Both reach the caller on the BillingError this throws, and callers switch on
// the code, never on the message.
//
// Every one of these calls needs a real token, and the caller must be an admin
// of the organization the body names — the body's organizationId is what the
// server authorises, not the header. The header is sent to match anyway, since
// the rest of the API scopes on it.
//
// Every BillingStatus a call returns is also kept in the billing store, so the
// screens that decide by it — the routes, the banners — never lag behind a card
// that was just saved or a plan that was just picked. The one exception is a
// plan whose caller asks to keep its status itself (createSubscription,
// changePlan).

import https from "../../base/utils/http";
import AuthService from "../../base/services/authentication.service";
import { BillingCode } from "./billingCodes";
import { useBillingStore } from "./billingStore";

const BASE = "/Payment";

// BILLING_BUSY means another billing change for the organization holds the
// lock and this one was not attempted, so it is safe to send again. A couple
// of spaced-out attempts cover the usual case — a webhook finishing — before
// the caller is told.
const BUSY_RETRY_DELAYS_MS = [1000, 2500];

const GENERIC_MESSAGE = "Something went wrong. Please try again.";

const STATUS_CARRYING_CODES = new Set([BillingCode.PAYMENT_FAILED, BillingCode.OUTSTANDING_DEBT]);

/** A billing call that failed, with what the server said about why. */
export class BillingError extends Error {
   constructor(message, { code = null, field = null, statusCode = null, envelope = null } = {}) {
      super(message || GENERIC_MESSAGE);
      this.name = "BillingError";
      this.code = code;
      this.field = field;
      this.statusCode = statusCode;
      this.envelope = envelope;
      // The server's wording, for showing next to a code — never for deciding by.
      this.serverMessage = message || null;
      // Two refusals carry the billing status in `data`, for the page to carry
      // on from: a payment the bank declined (409 PAYMENT_FAILED — what is still
      // owed, the bank's reason in paymentFailureMessage), and a new plan while
      // an ended annual plan still owes money (409 OUTSTANDING_DEBT — the debt as
      // amountDue).
      this.status =
         STATUS_CARRYING_CODES.has(code) && envelope?.data && typeof envelope.data === "object"
            ? envelope.data
            : null;
   }
}

const readMessage = (body) => {
   if (!body || typeof body !== "object") return null;
   if (typeof body.message === "string" && body.message) return body.message;
   if (Array.isArray(body.errors) && body.errors.length) return body.errors.filter(Boolean).join(" ");
   return null;
};

// A failure that came back without a code still has a status, and three of
// them say plainly what happened. A 401 in particular never carries a code: it
// is answered by the authentication middleware, before any billing code runs,
// with a bare { message: "Unauthorized" }.
const codeForStatus = (statusCode) => {
   switch (statusCode) {
      case 401:
         return BillingCode.UNAUTHENTICATED;
      case 403:
         return BillingCode.NOT_ORGANIZATION_ADMIN;
      case 429:
         return BillingCode.RATE_LIMITED;
      default:
         return null;
   }
};

/** Any failure — an axios rejection, a failed envelope — as a BillingError. */
export const toBillingError = (error) => {
   if (error instanceof BillingError) return error;

   const response = error?.response;

   // No response at all. An axios error without one is a request that never
   // got an answer — offline, timed out, blocked. Anything else is a bug on
   // this side, and is not dressed up as a connection problem.
   if (!response) {
      const failure = new BillingError(null, {
         code: error?.isAxiosError ? BillingCode.NETWORK_ERROR : null
      });
      failure.cause = error;
      return failure;
   }

   const body = response.data && typeof response.data === "object" ? response.data : null;
   const statusCode = response.status ?? body?.statusCode ?? null;

   return new BillingError(readMessage(body), {
      code: body?.code || codeForStatus(statusCode),
      field: body?.field || null,
      statusCode,
      envelope: body
   });
};

/** The payload of a successful envelope; throws on one that reports failure. */
export const readBillingEnvelope = (response) => {
   const envelope = response?.data;

   if (!envelope || typeof envelope !== "object") {
      throw new BillingError(null, { statusCode: response?.status ?? null });
   }

   if (envelope.isSuccess === false) {
      throw new BillingError(readMessage(envelope), {
         code: envelope.code || codeForStatus(envelope.statusCode),
         field: envelope.field || null,
         statusCode: envelope.statusCode ?? response.status ?? null,
         envelope
      });
   }

   return envelope.data ?? null;
};

const headersFor = (organizationId) => {
   const headers = {};

   if (organizationId) headers["X-OrganizationId"] = organizationId;

   // Nobody is signed in yet: a sign up between confirming its email and
   // picking its plan. confirmEmail handed it a token for exactly these calls;
   // it is not a session, so the request interceptor knows nothing about it,
   // and it is named here instead. A signed-in token always wins, and is left
   // to the interceptor so a refreshed one is picked up.
   if (!AuthService.getAccessToken()) {
      const signUpToken = AuthService.getSignUpToken();
      if (signUpToken) headers.Authorization = `Bearer ${signUpToken}`;
   }

   return headers;
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// `withMessage` also hands back the envelope's own sentence, for a call whose
// caller shows what the server said happened: `{ data, message }`.
async function request({ method, path, organizationId, data, params, withMessage = false }, attempt = 0) {
   try {
      const response = await https.request({
         method,
         url: `${BASE}/${path}`,
         data,
         params,
         headers: headersFor(organizationId)
      });

      const payload = readBillingEnvelope(response);
      return withMessage ? { data: payload, message: readMessage(response.data) } : payload;
   } catch (error) {
      const failure = toBillingError(error);

      if (failure.code === BillingCode.BILLING_BUSY && attempt < BUSY_RETRY_DELAYS_MS.length) {
         await wait(BUSY_RETRY_DELAYS_MS[attempt]);
         return request({ method, path, organizationId, data, params, withMessage }, attempt + 1);
      }

      throw failure;
   }
}

// A signed-in organization's status goes to the store. A sign up's does not:
// nobody is signed in to it yet, and the sign in that follows reports its own.
const remember = (organizationId, status) => {
   if (status && AuthService.isAuthenticated()) {
      useBillingStore.getState().setStatus(organizationId, status);
   }

   return status;
};

// A declined payment still leaves a status worth keeping: the card is saved, and
// what is owed has been read again. So does a plan refused for an unpaid debt:
// it is what holds the organization now. It is kept, and the refusal goes on.
const rememberRefusalStatus = (organizationId) => (failure) => {
   if (failure?.status) remember(organizationId, failure.status);
   throw failure;
};

const blankToNull = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);

/**
 * True for the answer the new flow's routes give while the flag is off for an
 * organization: HTTP 404 with an empty body. Told by the status and the
 * missing code alone; the body is never read for it. A 404 that does carry a
 * code (ORGANIZATION_NOT_FOUND) is the new flow answering, and a 401 or 403 is
 * about who is asking, never about the flag.
 */
export const isFlowSwitchedOff = (failure) => failure?.statusCode === 404 && !failure.code;

export const billingApi = {
   /** GET billing-profile — who invoices are made out to. */
   getBillingProfile: (organizationId) =>
      request({ method: "get", path: "billing-profile", organizationId, params: { organizationId } }),

   /** PUT billing-profile — returns the profile as saved, `isComplete` included. */
   saveBillingProfile: (profile) =>
      request({
         method: "put",
         path: "billing-profile",
         organizationId: profile.organizationId,
         data: profile
      }),

   /**
    * POST setup-intents — the SetupIntent a card is saved through. `consent` is
    * mandatory (consent.js). Returns { setupIntentId, clientSecret }.
    */
   createSetupIntent: ({ organizationId, consent }) =>
      request({
         method: "post",
         path: "setup-intents",
         organizationId,
         data: { organizationId, consent }
      }),

   /**
    * POST setup-intents/{id}/complete — after Stripe has confirmed the card. The
    * server makes it the default, applies any pending plan change, resumes a
    * paused subscription and pays what is owed (the status's `amountDue`).
    * Returns a BillingStatus whose `paymentOutcome` says how that payment went:
    * "succeeded", "requires_action" (3-D Secure, see paymentAction.js), or null
    * when nothing was owed. A declined one rejects: 409 PAYMENT_FAILED, with
    * the status it left on the BillingError (`status`), which is kept.
    */
   completeSetupIntent: ({ organizationId, setupIntentId }) =>
      request({
         method: "post",
         path: `setup-intents/${encodeURIComponent(setupIntentId)}/complete`,
         organizationId,
         data: { organizationId }
      }).then((status) => remember(organizationId, status), rememberRefusalStatus(organizationId)),

   /**
    * POST pay-outstanding — pay what is owed (`amountDue`) with the card already
    * saved: the next overdue invoice after 3-D Secure on another, or the same
    * card tried again. Answers like completeSetupIntent. 409 NO_SUBSCRIPTION
    * when there is nothing to pay for, 409 PAYMENT_METHOD_REQUIRED when no card
    * is saved.
    */
   payOutstanding: ({ organizationId }) =>
      request({
         method: "post",
         path: "pay-outstanding",
         organizationId,
         data: { organizationId }
      }).then((status) => remember(organizationId, status), rememberRefusalStatus(organizationId)),

   /**
    * GET billing-status — read fresh from Stripe. The only call that fills in
    * `actionClientSecret`.
    *
    * Also where the app learns that the flag was switched off again: the
    * organization is let go of (isFlowSwitchedOff), so every screen that
    * decides by the store goes back to the flow before this one, whichever
    * screen made the read. It still rejects, for the caller to stop on.
    */
   getBillingStatus: (organizationId) =>
      request({
         method: "get",
         path: "billing-status",
         organizationId,
         params: { organizationId }
      }).then(
         (status) => remember(organizationId, status),
         (failure) => {
            if (isFlowSwitchedOff(failure)) useBillingStore.getState().forget(organizationId);
            throw failure;
         }
      ),

   /**
    * POST subscriptions — start a subscription (a trial, when the packet has
    * one). Safe to repeat: a second call returns the same subscription.
    *
    * With no trial left and a card on file the first payment is taken at once,
    * and a declined one rejects as completeSetupIntent's does: 409
    * PAYMENT_FAILED with the status on the BillingError, for the caller to keep.
    * A plan cheaper than the one a cancelled subscription had is refused with
    * 409 DOWNGRADE_REQUIRES_SUPPORT, and any plan while an ended annual plan
    * still owes money with 409 OUTSTANDING_DEBT (the status, debt and all, on
    * the BillingError's `status`).
    *
    * `rememberStatus: false` hands the status back without keeping it, for a
    * caller with something to put in place before the routes act on it: the
    * level the server sets from the packet, which the billing page re-reads
    * first (billingSignIn.js, rememberNewSubscription).
    */
   createSubscription: (
      { organizationId, packetId, isMonthly, promoCode },
      { rememberStatus = true } = {}
   ) =>
      request({
         method: "post",
         path: "subscriptions",
         organizationId,
         data: { organizationId, packetId, isMonthly, promoCode: blankToNull(promoCode) }
      }).then(
         (status) => (rememberStatus ? remember(organizationId, status) : status),
         rememberStatus ? rememberRefusalStatus(organizationId) : undefined
      ),

   /**
    * POST change-plan — `when` is "now" or "period_end".
    *
    * "now" changes the plan at once; the difference an upgrade costs is charged
    * straight away, and the answer may ask for 3-D Secure (`requiresAction`).
    * "period_end" changes nothing yet: the answer carries the change as
    * `pendingPlanChange`, and the server applies it when the contract ends.
    * During a trial every change is "now". Which one a change is follows from
    * the plans (planChanges.js); the server applies the same rules.
    *
    * `rememberStatus: false` as for createSubscription: a plan of the other kind
    * of account changes the level, which the caller re-reads first.
    */
   changePlan: (
      { organizationId, packetId, isMonthlySubscription, when, promoCode },
      { rememberStatus = true } = {}
   ) =>
      request({
         method: "post",
         path: "change-plan",
         organizationId,
         data: {
            organizationId,
            packetId,
            isMonthlySubscription,
            when,
            promoCode: blankToNull(promoCode)
         }
      }).then(
         (status) => (rememberStatus ? remember(organizationId, status) : status),
         rememberStatus ? rememberRefusalStatus(organizationId) : undefined
      ),

   /**
    * POST cancel-plan-change — drop a plan change waiting for the end of the
    * contract (`pendingPlanChange`). The plan in force carries on. Asking with
    * nothing pending changes nothing. It answers on either flow: the old flow's
    * page asks with `rememberStatus: false`, since a status kept for its
    * organization would switch it to the new flow's screens.
    */
   cancelPlanChange: ({ organizationId }, { rememberStatus = true } = {}) =>
      request({
         method: "post",
         path: "cancel-plan-change",
         organizationId,
         data: { organizationId }
      }).then((status) => (rememberStatus ? remember(organizationId, status) : status)),

   /**
    * POST cancel — cancel the subscription. A trial or a paid period runs to its
    * end (`cancelAtPeriodEnd`, access stays "full"), and an active annual plan
    * before its last month runs, billed monthly, to the end of its 12-month
    * commitment (the answer's `cancelAt`); a subscription with nothing
    * paid for the period (past_due, unpaid, incomplete, paused) ends now, and a
    * plan still waiting for a card is dropped. Asking again changes nothing.
    * The reason codes are the ones cancelStripeSubscription takes.
    *
    * Resolves `{ status, message }`: the status it left, which replaces the one
    * held, and the server's own sentence for what happened.
    */
   cancel: ({ organizationId, reasonOfCancellation, customReasonOfCancellation }) =>
      request({
         method: "post",
         path: "cancel",
         organizationId,
         data: {
            organizationId,
            reasonOfCancellation: reasonOfCancellation ?? null,
            customReasonOfCancellation: blankToNull(customReasonOfCancellation)
         },
         withMessage: true
      }).then(({ data: status, message }) => ({ status: remember(organizationId, status), message })),

   /** POST undo-cancel — keep a subscription that was set to end with its period. */
   undoCancel: ({ organizationId }) =>
      request({
         method: "post",
         path: "undo-cancel",
         organizationId,
         data: { organizationId }
      }).then((status) => remember(organizationId, status)),

   /** POST promo-codes/preview — what a code would take off the first paid invoice. */
   previewPromoCode: ({ organizationId, packetId, isMonthlySubscription, promoCode }) =>
      request({
         method: "post",
         path: "promo-codes/preview",
         organizationId,
         data: {
            organizationId,
            packetId,
            isMonthlySubscription,
            promoCode: blankToNull(promoCode)
         }
      })
};

/** The `when` values change-plan takes. */
export const PlanChangeTiming = Object.freeze({
   NOW: "now",
   PERIOD_END: "period_end"
});
